// src/components/manager-v2/TeamReports.js
import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Download, Calendar, Filter, BarChart3, TrendingUp, Loader, AlertCircle, Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import reportsService from '../../services/reports.service';
import holidayService from '../../services/holiday.service';
import { api } from '../../services/api';

const TeamReports = () => {
    const { user } = useAuth();
    const { employees } = useEmployees();
    const [selectedReport, setSelectedReport] = useState('attendance');
    const [selectedPeriod, setSelectedPeriod] = useState('this_month');
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Get team member IDs - employees who report to this manager
    const teamMemberIds = useMemo(() => {
        if (!user?.id || !employees) return new Set();
        const teamMembers = employees.filter(emp =>
            emp.managerId === user.id || emp.reporting_manager_id === user.id
        );
        return new Set(teamMembers.map(emp => emp.id));
    }, [user?.id, employees]);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        manager: '',
        department: '',
        designation: '',
        rating: ''
    });

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const reportTypes = [
        { id: 'attendance', name: 'Attendance Report', icon: Calendar },
        { id: 'performance', name: 'Performance Report', icon: TrendingUp },
        { id: 'leave', name: 'Leave Report', icon: FileText },
        { id: 'productivity', name: 'Productivity Report', icon: BarChart3 }
    ];

    const periods = [
        { id: 'this_week', name: 'This Week' },
        { id: 'this_month', name: 'This Month' },
        { id: 'last_month', name: 'Last Month' },
        { id: 'this_quarter', name: 'This Quarter' },
        { id: 'this_year', name: 'This Year' }
    ];

    // Calculate working days in a period (excluding weekends based on weekly_off)
    const calculateWorkingDays = (startDate, endDate, weeklyOff) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const weeklyOffDays = parseWeeklyOffDays(weeklyOff);

        let workingDays = 0;
        const current = new Date(start);

        while (current <= end) {
            const dayOfWeek = current.getDay();
            if (!weeklyOffDays.includes(dayOfWeek)) {
                workingDays++;
            }
            current.setDate(current.getDate() + 1);
        }

        return workingDays;
    };

    // Parse weekly off string to day numbers
    const parseWeeklyOffDays = (weeklyOffStr) => {
        if (!weeklyOffStr) return [0, 6]; // Default: Sunday and Saturday

        const weeklyOffStr_lower = String(weeklyOffStr).toLowerCase();
        const dayMap = {
            'sunday': 0, 'sundays': 0, 'sun': 0,
            'monday': 1, 'mondays': 1, 'mon': 1,
            'tuesday': 2, 'tuesdays': 2, 'tue': 2, 'tues': 2,
            'wednesday': 3, 'wednesdays': 3, 'wed': 3,
            'thursday': 4, 'thursdays': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
            'friday': 5, 'fridays': 5, 'fri': 5,
            'saturday': 6, 'saturdays': 6, 'sat': 6
        };

        const cleanedStr = weeklyOffStr_lower
            .replace(/\b(only|off|day|days)\b/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        const parts = cleanedStr.split(/[,&]|\band\b|\s+/);
        const days = [];

        parts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed && dayMap.hasOwnProperty(trimmed)) {
                const dayNum = dayMap[trimmed];
                if (!days.includes(dayNum)) {
                    days.push(dayNum);
                }
            }
        });

        return days.length > 0 ? days : [0, 6]; // Default to Sunday and Saturday if parsing fails
    };

    // Get period date range based on selected period
    const getPeriodDates = (period) => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();

        switch (period) {
            case 'this_week': {
                const day = now.getDay();
                const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Start from Monday
                const startDate = new Date(year, month, diff);
                const endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 6);
                return { startDate, endDate };
            }
            case 'this_month':
                return {
                    startDate: new Date(year, month, 1),
                    endDate: new Date(year, month + 1, 0)
                };
            case 'last_month':
                return {
                    startDate: new Date(year, month - 1, 1),
                    endDate: new Date(year, month, 0)
                };
            case 'this_quarter': {
                const quarterStartMonth = Math.floor(month / 3) * 3;
                return {
                    startDate: new Date(year, quarterStartMonth, 1),
                    endDate: new Date(year, quarterStartMonth + 3, 0)
                };
            }
            case 'this_year':
                return {
                    startDate: new Date(year, 0, 1),
                    endDate: new Date(year, 11, 31)
                };
            default:
                return {
                    startDate: new Date(year, month, 1),
                    endDate: new Date(year, month + 1, 0)
                };
        }
    };

    // Enhance attendance data with calculated fields
    const enhanceAttendanceData = async (data) => {
        const { startDate, endDate } = getPeriodDates(selectedPeriod);

        // Fetch holiday data for the period
        const holidays = await fetchHolidaysForPeriod(startDate, endDate);

        // Fetch leave data for all team members
        const leaveData = await fetchLeavesForPeriod(startDate, endDate);

        return data.map(emp => {
            // Get employee info from context
            const employee = employees?.find(e => e.id === emp.id || e.id === emp.employee_id);
            const weeklyOff = employee?.weekly_off || 'Sunday';
            const employeeId = emp.id || emp.employee_id;

            // Calculate working days (total days - weekends)
            const totalWorkingDays = calculateWorkingDays(startDate, endDate, weeklyOff);

            // Get present days from API response
            const presentDays = parseInt(emp.present_days) || 0;

            // Calculate leave days from fetched leave data
            const employeeLeaves = leaveData.filter(leave =>
                (leave.employee_id === employeeId || leave.id === employeeId) &&
                leave.status === 'approved'
            );

            const leaveDays = employeeLeaves.reduce((total, leave) => {
                const leaveStart = new Date(leave.from_date || leave.start_date);
                const leaveEnd = new Date(leave.to_date || leave.end_date);

                // Ensure dates are within the period
                const effectiveStart = leaveStart < startDate ? startDate : leaveStart;
                const effectiveEnd = leaveEnd > endDate ? endDate : leaveEnd;

                // Count days excluding weekends
                let count = 0;
                const current = new Date(effectiveStart);
                const weeklyOffDays = parseWeeklyOffDays(weeklyOff);

                while (current <= effectiveEnd) {
                    const dayOfWeek = current.getDay();
                    if (!weeklyOffDays.includes(dayOfWeek)) {
                        count++;
                    }
                    current.setDate(current.getDate() + 1);
                }

                return total + count;
            }, 0);

            // Calculate holidays (company holidays that fall on working days)
            const weeklyOffDays = parseWeeklyOffDays(weeklyOff);
            const holidayDays = holidays.filter(holiday => {
                const holidayDate = new Date(holiday.date);
                const dayOfWeek = holidayDate.getDay();
                return !weeklyOffDays.includes(dayOfWeek); // Only count holidays on working days
            }).length;

            // Calculate absent days = Working days - Present - Leave - Holidays
            const absentDays = Math.max(0, totalWorkingDays - presentDays - leaveDays - holidayDays);

            // Calculate attendance percentage = (Present / (Working Days - Holidays)) * 100
            const effectiveWorkingDays = totalWorkingDays - holidayDays;
            const attendancePercentage = effectiveWorkingDays > 0
                ? ((presentDays / effectiveWorkingDays) * 100).toFixed(1)
                : '0.00';

            return {
                ...emp,
                total_working_days: totalWorkingDays,
                working_days: totalWorkingDays,
                present_days: presentDays,
                absent_days: absentDays,
                leave_days: leaveDays,
                holiday_days: holidayDays,
                attendance_percentage: attendancePercentage,
                total_days: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
            };
        });
    };

    // Fetch holidays for a period
    const fetchHolidaysForPeriod = async (startDate, endDate) => {
        try {
            const year = startDate.getFullYear();
            const allHolidays = await holidayService.getAllHolidays(year);

            // Filter holidays that fall within the period
            return allHolidays.filter(holiday => {
                const holidayDate = new Date(holiday.date);
                return holidayDate >= startDate && holidayDate <= endDate;
            });
        } catch (error) {
            console.error('Error fetching holidays:', error);
            return [];
        }
    };

    // Fetch leaves for a period
    const fetchLeavesForPeriod = async (startDate, endDate) => {
        try {
            const response = await api.get('/api/leaves/all-requests');
            const allLeaves = response.data.leaves || response.data.requests || response.data || [];

            // Filter leaves that overlap with the period
            return allLeaves.filter(leave => {
                const leaveStart = new Date(leave.from_date || leave.start_date);
                const leaveEnd = new Date(leave.to_date || leave.end_date);

                // Check if leave overlaps with the period
                return leaveStart <= endDate && leaveEnd >= startDate;
            });
        } catch (error) {
            console.error('Error fetching leaves:', error);
            return [];
        }
    };

    // Fetch report data and filter by team members
    const fetchReport = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check if manager has team members
            if (teamMemberIds.size === 0) {
                setReportData({
                    data: [],
                    summary: {
                        totalEmployees: 0,
                        avgAttendance: 0,
                        totalPresent: 0,
                        totalAbsent: 0,
                        totalRequests: 0,
                        approvedRequests: 0,
                        pendingRequests: 0,
                        avgRating: 0,
                        avgProductivity: 0,
                        topPerformers: 0
                    }
                });
                setLoading(false);
                return;
            }

            let response;
            const params = { period: selectedPeriod };

            switch (selectedReport) {
                case 'attendance':
                    response = await reportsService.getAttendanceReport(params);
                    break;
                case 'performance':
                    response = await reportsService.getPerformanceReport(params);
                    break;
                case 'leave':
                    response = await reportsService.getLeaveReport(params);
                    break;
                case 'productivity':
                    response = await reportsService.getProductivityReport(params);
                    break;
                default:
                    response = await reportsService.getAttendanceReport(params);
            }

            // Filter data to only include team members
            if (response && response.data) {
                let filteredData = response.data.filter(emp =>
                    teamMemberIds.has(emp.id) || teamMemberIds.has(emp.employee_id)
                );

                // Enhance attendance data with calculated fields if it's attendance report
                if (selectedReport === 'attendance') {
                    filteredData = await enhanceAttendanceData(filteredData);
                }

                // Recalculate summary based on filtered team data
                const teamSummary = calculateTeamSummary(filteredData, selectedReport);

                setReportData({
                    ...response,
                    data: filteredData,
                    summary: teamSummary
                });
            } else {
                setReportData(response);
            }
        } catch (err) {
            console.error('Error fetching report:', err);
            setError('Failed to load report data');
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    // Calculate summary statistics for team data
    const calculateTeamSummary = (data, reportType) => {
        if (!data || data.length === 0) {
            return {
                totalEmployees: 0,
                avgAttendance: 0,
                totalPresent: 0,
                totalAbsent: 0,
                totalRequests: 0,
                approvedRequests: 0,
                pendingRequests: 0,
                avgRating: 0,
                avgProductivity: 0,
                topPerformers: 0,
                avgAttendanceRate: 0
            };
        }

        switch (reportType) {
            case 'attendance': {
                const totalPresent = data.reduce((sum, emp) => sum + (parseInt(emp.present_days) || 0), 0);
                const totalAbsent = data.reduce((sum, emp) => sum + (parseInt(emp.absent_days) || 0), 0);
                const totalLeave = data.reduce((sum, emp) => sum + (parseInt(emp.leave_days) || 0), 0);
                const totalHolidays = data.reduce((sum, emp) => sum + (parseInt(emp.holiday_days) || 0), 0);
                const totalWorkingDays = data.reduce((sum, emp) => sum + (parseInt(emp.total_working_days) || parseInt(emp.working_days) || 0), 0);

                // Calculate average attendance based on working days
                const avgAttendance = data.length > 0
                    ? (data.reduce((sum, emp) => sum + (parseFloat(emp.attendance_percentage) || 0), 0) / data.length).toFixed(1)
                    : 0;

                return {
                    totalEmployees: data.length,
                    avgAttendance,
                    totalPresent,
                    totalAbsent,
                    totalLeave,
                    totalHolidays,
                    totalWorkingDays
                };
            }
            case 'leave': {
                const totalRequests = data.reduce((sum, emp) => sum + (parseInt(emp.total_requests) || 0), 0);
                const approvedRequests = data.reduce((sum, emp) => sum + (parseInt(emp.approved_requests) || 0), 0);
                const pendingRequests = data.reduce((sum, emp) => sum + (parseInt(emp.pending_requests) || 0), 0);
                const rejectedRequests = data.reduce((sum, emp) => sum + (parseInt(emp.rejected_requests) || 0), 0);
                const totalLeaveDays = data.reduce((sum, emp) => sum + (parseInt(emp.total_leave_days) || 0), 0);
                return {
                    totalEmployees: data.length,
                    totalRequests,
                    approvedRequests,
                    pendingRequests,
                    rejectedRequests,
                    totalLeaveDays
                };
            }
            case 'performance': {
                const avgRating = data.length > 0
                    ? (data.reduce((sum, emp) => sum + (parseFloat(emp.rating) || 0), 0) / data.length).toFixed(1)
                    : 0;
                const avgProductivity = data.length > 0
                    ? Math.round(data.reduce((sum, emp) => sum + (parseFloat(emp.productivity) || 0), 0) / data.length)
                    : 0;
                const topPerformers = data.filter(emp => (parseFloat(emp.rating) || 0) >= 4.0).length;
                const needsImprovement = data.filter(emp => (parseFloat(emp.rating) || 0) < 2.5).length;
                return {
                    totalEmployees: data.length,
                    avgRating,
                    avgProductivity,
                    topPerformers,
                    needsImprovement
                };
            }
            case 'productivity': {
                const avgProductivity = data.length > 0
                    ? Math.round(data.reduce((sum, emp) => sum + (parseFloat(emp.productivity_score) || 0), 0) / data.length)
                    : 0;
                const avgAttendanceRate = data.length > 0
                    ? (data.reduce((sum, emp) => sum + (parseFloat(emp.attendance_rate) || 0), 0) / data.length).toFixed(1)
                    : 0;
                return {
                    totalEmployees: data.length,
                    avgProductivity,
                    avgAttendanceRate
                };
            }
            default:
                return { totalEmployees: data.length };
        }
    };

    // Fetch report when type, period, or team members change
    useEffect(() => {
        if (teamMemberIds.size > 0 || employees?.length > 0) {
            fetchReport();
        }
        // Reset filters and search when report type changes
        setSearchTerm('');
        setFilters({ manager: '', department: '', designation: '', rating: '' });
        setSortConfig({ key: null, direction: 'asc' });
    }, [selectedReport, selectedPeriod, teamMemberIds.size]);

    // Get unique values for filters
    const getUniqueValues = (key) => {
        if (!reportData || !reportData.data) return [];
        const values = reportData.data
            .map(emp => {
                if (key === 'manager') {
                    return emp.manager_first_name ? `${emp.manager_first_name} ${emp.manager_last_name}` : '';
                }
                if (key === 'department') {
                    return emp.department_name;
                }
                return emp[key];
            })
            .filter(val => val && val !== 'N/A');
        return [...new Set(values)].sort();
    };

    // Filter and search data
    const filteredData = useMemo(() => {
        if (!reportData || !reportData.data) return [];

        let filtered = reportData.data.filter(employee => {
            // Search filter
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = !searchTerm ||
                employee.first_name?.toLowerCase().includes(searchLower) ||
                employee.last_name?.toLowerCase().includes(searchLower) ||
                employee.employee_code?.toLowerCase().includes(searchLower);

            if (!matchesSearch) return false;

            // Manager filter
            if (filters.manager) {
                const managerName = employee.manager_first_name
                    ? `${employee.manager_first_name} ${employee.manager_last_name}`
                    : '';
                if (managerName !== filters.manager) return false;
            }

            // Department filter
            if (filters.department && employee.department_name !== filters.department) {
                return false;
            }

            // Designation filter
            if (filters.designation && employee.designation !== filters.designation) {
                return false;
            }

            // Rating filter (for performance report)
            if (filters.rating) {
                const rating = parseFloat(employee.rating) || 0;
                const [min, max] = filters.rating.split('-').map(Number);
                if (rating < min || rating > max) return false;
            }

            return true;
        });

        // Sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle special cases
                if (sortConfig.key === 'name') {
                    aVal = `${a.first_name} ${a.last_name}`;
                    bVal = `${b.first_name} ${b.last_name}`;
                } else if (sortConfig.key === 'manager') {
                    aVal = a.manager_first_name ? `${a.manager_first_name} ${a.manager_last_name}` : '';
                    bVal = b.manager_first_name ? `${b.manager_first_name} ${b.manager_last_name}` : '';
                }

                // Convert to numbers if possible
                const aNum = parseFloat(aVal);
                const bNum = parseFloat(bVal);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }

                // String comparison
                aVal = String(aVal || '').toLowerCase();
                bVal = String(bVal || '').toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [reportData, searchTerm, filters, sortConfig]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Clear filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({ manager: '', department: '', designation: '', rating: '' });
    };

    // Handle download
    const handleDownload = () => {
        if (!filteredData || filteredData.length === 0) {
            alert('No data to download');
            return;
        }

        reportsService.exportToCSV(
            selectedReport,
            filteredData,
            reportData.summary,
            periods.find(p => p.id === selectedPeriod)?.name || selectedPeriod
        );
    };

    // Render sort icon
    const renderSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp className="w-3 h-3 ml-1" />
            : <ArrowDown className="w-3 h-3 ml-1" />;
    };

    // Render summary cards based on report type
    const renderSummaryCards = () => {
        if (!reportData || !reportData.summary) return null;

        const { summary } = reportData;

        switch (selectedReport) {
            case 'attendance':
                return (
                    <>
                        <SummaryCard title="Working Days" value={summary.totalWorkingDays || 'N/A'} subtitle="Total period" />
                        <SummaryCard title="Present Days" value={summary.totalPresent} subtitle={`${summary.avgAttendance}% avg`} />
                        <SummaryCard title="Absent Days" value={summary.totalAbsent} />
                        <SummaryCard title="Leave Days" value={summary.totalLeave} />
                        <SummaryCard title="Holidays" value={summary.totalHolidays || 0} />
                    </>
                );
            case 'leave':
                return (
                    <>
                        <SummaryCard title="Total Employees" value={filteredData.length} subtitle={`of ${summary.totalEmployees}`} />
                        <SummaryCard title="Total Requests" value={summary.totalRequests} />
                        <SummaryCard title="Approved" value={summary.approvedRequests} />
                        <SummaryCard title="Pending" value={summary.pendingRequests} />
                    </>
                );
            case 'performance':
                return (
                    <>
                        <SummaryCard title="Showing" value={filteredData.length} subtitle={`of ${summary.totalEmployees}`} />
                        <SummaryCard title="Avg Rating" value={`${summary.avgRating}/5.0`} />
                        <SummaryCard title="Avg Productivity" value={`${summary.avgProductivity}%`} />
                        <SummaryCard title="Top Performers" value={summary.topPerformers} />
                    </>
                );
            case 'productivity':
                return (
                    <>
                        <SummaryCard title="Total Employees" value={filteredData.length} subtitle={`of ${summary.totalEmployees}`} />
                        <SummaryCard title="Avg Productivity" value={`${summary.avgProductivity}%`} />
                        <SummaryCard title="Avg Attendance" value={`${summary.avgAttendanceRate}%`} />
                    </>
                );
            default:
                return null;
        }
    };

    // Render filters based on report type
    const renderFilters = () => {
        const managers = getUniqueValues('manager');
        const departments = getUniqueValues('department');
        const designations = getUniqueValues('designation');

        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters</h3>
                    </div>
                    {(searchTerm || Object.values(filters).some(v => v)) && (
                        <button
                            onClick={clearFilters}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                            <X className="w-3 h-3" />
                            Clear All
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Search Employee
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Name or code..."
                                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Manager Filter (all reports) */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Reporting Manager
                        </label>
                        <select
                            value={filters.manager}
                            onChange={(e) => setFilters(prev => ({ ...prev, manager: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="">All Managers</option>
                            {managers.filter(m => m).map(manager => (
                                <option key={manager} value={manager}>{manager}</option>
                            ))}
                        </select>
                    </div>

                    {/* Report-specific filters */}
                    {(selectedReport === 'attendance' || selectedReport === 'leave') && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Department
                            </label>
                            <select
                                value={filters.department}
                                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {(selectedReport === 'performance' || selectedReport === 'productivity') && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Designation
                            </label>
                            <select
                                value={filters.designation}
                                onChange={(e) => setFilters(prev => ({ ...prev, designation: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="">All Designations</option>
                                {designations.map(desig => (
                                    <option key={desig} value={desig}>{desig}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedReport === 'performance' && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Rating Range
                            </label>
                            <select
                                value={filters.rating}
                                onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="">All Ratings</option>
                                <option value="4.5-5">Excellent (4.5-5.0)</option>
                                <option value="3.5-4.5">Good (3.5-4.5)</option>
                                <option value="2.5-3.5">Average (2.5-3.5)</option>
                                <option value="0-2.5">Below Average (0-2.5)</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* Results count */}
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                    Showing <span className="font-semibold text-gray-900 dark:text-white">{filteredData.length}</span> of{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">{reportData?.data?.length || 0}</span> employees
                </div>
            </div>
        );
    };

    // Render table rows based on report type
    const renderTableRows = () => {
        if (!filteredData || filteredData.length === 0) return null;

        return filteredData.map((employee) => (
            <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                            {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {employee.first_name} {employee.last_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{employee.employee_code}</p>
                            {employee.level && employee.level > 1 && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">Level {employee.level}</p>
                            )}
                        </div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {employee.manager_first_name ? (
                        <div>
                            <p className="font-medium">{employee.manager_first_name} {employee.manager_last_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{employee.manager_employee_code}</p>
                        </div>
                    ) : (
                        <span className="text-gray-400 dark:text-gray-500">Direct Report</span>
                    )}
                </td>
                {selectedReport === 'attendance' && renderAttendanceColumns(employee)}
                {selectedReport === 'leave' && renderLeaveColumns(employee)}
                {selectedReport === 'performance' && renderPerformanceColumns(employee)}
                {selectedReport === 'productivity' && renderProductivityColumns(employee)}
            </tr>
        ));
    };

    const renderAttendanceColumns = (employee) => {
        const workingDays = employee.total_working_days || employee.working_days || 0;
        const presentDays = employee.present_days || 0;
        const absentDays = employee.absent_days || 0;
        const leaveDays = employee.leave_days || 0;
        const holidayDays = employee.holiday_days || 0;
        const totalDays = employee.total_days || 0;

        return (
            <>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.department_name || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 font-medium">{workingDays}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">{presentDays}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-medium">{absentDays}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 font-medium">{leaveDays}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 dark:text-orange-400 font-medium">{holidayDays}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.avg_work_hours || '0.00'} hrs</td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-semibold ${
                        parseFloat(employee.attendance_percentage) >= 90 ? 'text-green-600 dark:text-green-400' :
                        parseFloat(employee.attendance_percentage) >= 75 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-red-600 dark:text-red-400'
                    }`}>
                        {employee.attendance_percentage || '0.00'}%
                    </span>
                </td>
            </>
        );
    };

    const renderLeaveColumns = (employee) => (
        <>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.department_name || 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.total_requests || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">{employee.approved_requests || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 dark:text-yellow-400 font-medium">{employee.pending_requests || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-medium">{employee.rejected_requests || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">{employee.total_leave_days || 0}</td>
        </>
    );

    const renderPerformanceColumns = (employee) => {
        const rating = employee.rating ? parseFloat(employee.rating) : 0;
        const totalGoals = parseInt(employee.total_goals || 0);
        const completedGoals = parseInt(employee.completed_goals || 0);
        const goalsProgress = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;

        return (
            <>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.designation || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">
                    {rating > 0 ? rating.toFixed(1) : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{completedGoals}/{totalGoals}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2" style={{ maxWidth: '100px' }}>
                            <div
                                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full"
                                style={{ width: `${goalsProgress}%` }}
                            />
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white">{goalsProgress}%</span>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">{employee.productivity || 0}%</td>
            </>
        );
    };

    const renderProductivityColumns = (employee) => {
        const productivityScore = employee.productivity_score || 0;

        return (
            <>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.designation || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.working_days || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">{employee.present_days || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.avg_hours_per_day || '0.00'} hrs</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{employee.attendance_rate || '0.00'}%</td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2" style={{ maxWidth: '100px' }}>
                            <div
                                className={`h-2 rounded-full ${
                                    productivityScore >= 80 ? 'bg-green-600 dark:bg-green-500' :
                                    productivityScore >= 60 ? 'bg-yellow-600 dark:bg-yellow-500' :
                                    'bg-red-600 dark:bg-red-500'
                                }`}
                                style={{ width: `${productivityScore}%` }}
                            />
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white font-medium">{productivityScore}%</span>
                    </div>
                </td>
            </>
        );
    };

    // Get table headers based on report type
    const getTableHeaders = () => {
        const SortableHeader = ({ label, sortKey }) => (
            <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort(sortKey)}
            >
                <div className="flex items-center">
                    {label}
                    {renderSortIcon(sortKey)}
                </div>
            </th>
        );

        const baseHeaders = (
            <>
                <SortableHeader label="Employee" sortKey="name" />
                <SortableHeader label="Reporting Manager" sortKey="manager" />
            </>
        );

        switch (selectedReport) {
            case 'attendance':
                return (
                    <>
                        {baseHeaders}
                        <SortableHeader label="Department" sortKey="department_name" />
                        <SortableHeader label="Working Days" sortKey="total_working_days" />
                        <SortableHeader label="Present" sortKey="present_days" />
                        <SortableHeader label="Absent" sortKey="absent_days" />
                        <SortableHeader label="Leave" sortKey="leave_days" />
                        <SortableHeader label="Holidays" sortKey="holiday_days" />
                        <SortableHeader label="Avg Hours" sortKey="avg_work_hours" />
                        <SortableHeader label="Attendance %" sortKey="attendance_percentage" />
                    </>
                );
            case 'leave':
                return (
                    <>
                        {baseHeaders}
                        <SortableHeader label="Department" sortKey="department_name" />
                        <SortableHeader label="Total Requests" sortKey="total_requests" />
                        <SortableHeader label="Approved" sortKey="approved_requests" />
                        <SortableHeader label="Pending" sortKey="pending_requests" />
                        <SortableHeader label="Rejected" sortKey="rejected_requests" />
                        <SortableHeader label="Leave Days" sortKey="total_leave_days" />
                    </>
                );
            case 'performance':
                return (
                    <>
                        {baseHeaders}
                        <SortableHeader label="Designation" sortKey="designation" />
                        <SortableHeader label="Rating" sortKey="rating" />
                        <SortableHeader label="Goals" sortKey="completed_goals" />
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Progress</th>
                        <SortableHeader label="Productivity" sortKey="productivity" />
                    </>
                );
            case 'productivity':
                return (
                    <>
                        {baseHeaders}
                        <SortableHeader label="Designation" sortKey="designation" />
                        <SortableHeader label="Working Days" sortKey="working_days" />
                        <SortableHeader label="Present Days" sortKey="present_days" />
                        <SortableHeader label="Avg Hours" sortKey="avg_hours_per_day" />
                        <SortableHeader label="Attendance Rate" sortKey="attendance_rate" />
                        <SortableHeader label="Productivity" sortKey="productivity_score" />
                    </>
                );
            default:
                return baseHeaders;
        }
    };

    // Empty state when no team members
    if (!loading && teamMemberIds.size === 0 && employees?.length > 0) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Reports</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Generate and download team reports with advanced filtering
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Members Found</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        You don't have any team members assigned to you yet. Reports will be available once you have team members.
                    </p>
                    <button
                        onClick={() => window.location.href = 'mailto:hr@company.com?subject=Team Assignment Request'}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Contact HR for Team Assignment
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Reports</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Generate and download reports for your team ({teamMemberIds.size} members)
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Report Type and Period Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Report Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Report Type
                        </label>
                        <select
                            value={selectedReport}
                            onChange={(e) => setSelectedReport(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {reportTypes.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Period */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Period
                        </label>
                        <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {periods.map(period => (
                                <option key={period.id} value={period.id}>{period.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Actions */}
                    <div className="flex items-end">
                        <button
                            onClick={handleDownload}
                            disabled={loading || !filteredData || filteredData.length === 0}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="w-4 h-4" />
                            Download Report
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                </div>
            )}

            {/* Filters */}
            {!loading && reportData && renderFilters()}

            {/* Report Summary Cards */}
            {!loading && reportData && (
                <div className={`grid grid-cols-1 gap-4 ${selectedReport === 'attendance' ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                    {renderSummaryCards()}
                </div>
            )}

            {/* Report Table */}
            {!loading && reportData && filteredData && filteredData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {reportTypes.find(r => r.id === selectedReport)?.name} - {periods.find(p => p.id === selectedPeriod)?.name}
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    {getTableHeaders()}
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {renderTableRows()}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!loading && reportData && filteredData && filteredData.length === 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Data Available</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                        {searchTerm || Object.values(filters).some(v => v)
                            ? 'No employees match your search or filter criteria. Try adjusting your filters.'
                            : 'No data available for the selected report and period.'}
                    </p>
                </div>
            )}
        </div>
    );
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {value}
            {subtitle && <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">{subtitle}</span>}
        </p>
    </div>
);

export default TeamReports;
